import type { Locale } from "next-intl"
import { config } from "@/config"
import { env } from "@/env/client"

export function getUrlWithParams(pathname: string, params: URLSearchParams) {
  const paramsString = params.toString()

  if (!paramsString) return pathname

  return `${pathname}?${paramsString}`
}

export function getAbsoluteUrl(path?: string) {
  if (!path) return env.NEXT_PUBLIC_BASE_URL

  // Return early, if path already is an absolute URL (http://, https://, ftp://, mailto:, etc.)
  if (/^([a-z][a-z\d+.-]*):\/\/|^(mailto|tel|sms):/i.test(path)) return path

  return path.startsWith("/")
    ? `${env.NEXT_PUBLIC_BASE_URL}${path}`
    : `${env.NEXT_PUBLIC_BASE_URL}/${path}`
}

export function getLocalizedPath(locale: Locale, path?: string, alwaysAddLocale?: boolean) {
  const noPrefix =
    !alwaysAddLocale &&
    locale === config.i18n.defaultLocale &&
    config.i18n.localePrefixMode === "as-needed"

  const prefix = noPrefix ? "" : config.i18n.localePrefixes[locale]

  if (!path) return prefix

  return path.startsWith("/") ? `${prefix}${path}` : `${prefix}/${path}`
}

export function getAbsoluteLocalizedUrl(locale: Locale, path?: string, alwaysAddLocale?: boolean) {
  // Return early, if path already is an absolute URL (http://, https://, ftp://, mailto:, etc.)
  if (path && /^([a-z][a-z\d+.-]*):\/\/|^(mailto|tel|sms):/i.test(path)) return path

  const localizedPath = getLocalizedPath(locale, path, alwaysAddLocale)

  return `${env.NEXT_PUBLIC_BASE_URL}${localizedPath}`
}

export function getTriggerDevRunsUrl({
  organizationId,
  userId,
}: {
  organizationId?: string
  userId?: string
}) {
  const triggerEnv = env.NEXT_PUBLIC_DEVELOPMENT_MODE
    ? "dev"
    : env.NEXT_PUBLIC_PREVIEW_MODE
      ? "stg"
      : "prod"

  const baseUrl = `https://cloud.trigger.dev/orgs/fv-ventures-ug-587c/projects/backlinkgpt-kRY1/env/${triggerEnv}/runs`
  const params = new URLSearchParams()

  if (userId) {
    params.append("tags", userId)
  }

  if (organizationId) {
    params.append("tags", organizationId)
  }

  return getUrlWithParams(baseUrl, params)
}

export function getPolarCustomerUrl({ customerId }: { customerId?: string | null }) {
  if (!customerId) return null

  if (env.NEXT_PUBLIC_PRODUCTION_MODE)
    return `https://polar.sh/dashboard/backlinkgpt/customers?customerId=${customerId}`

  return `https://sandbox.polar.sh/dashboard/backlinkgpt/customers?customerId=${customerId}`
}
